import { api } from "@/modules/api";
import { windowModule } from "@/modules/window";
import { BillingStrategyEnum } from "@/store/pages/SettingsSubscriptionManagementPageStore/types";
import type { AppStore } from "@/store/AppStore";
import type { components } from "@/__openapi__/backend/schema";
import type { Location } from "react-router-dom";
import { BaseError } from "@/domains/errors";
import { buildOriginalSubscriptionPlanMetricsPayload } from "@/domains/metrics/monetization";
import {
  dismissReminderShillUpsellAttributionContext,
  recordReminderShillUpsellAttribution,
  writePendingReminderShillUpsellAttribution,
  type ReminderShillUpsellAttributionContext,
} from "@/modules/reminder-shill-upsell-attribution";
import {
  buildStripeBillingReturnUrl,
  buildStripeSuccessUrl,
  buildStripeCancelUrl,
} from "./query-params";
import {
  StripeCheckoutContext,
  StripeCheckoutSource,
  type StripeCheckoutOnboardingStep,
} from "./query-params";
import { isAppleProvider, isHouseProvider, isStripeProvider } from "./provider";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { getRewardfulReferral } from "@/modules/rewardful";
import { SubscriptionPlanKind, type SubscriptionPlanVariant } from "@/store/subscriptions/types";
import { toastModule } from "@/modules/toast";
import { isCommercialRolloutActive, type CommercialPlanKey } from "@/modules/commercial-plans";
import { SettingsTabId } from "@/store/pages/SettingsPageStore";

interface CreateCheckoutSessionParams {
  billingStrategy: BillingStrategyEnum;
  checkoutContext?: StripeCheckoutContext;
  checkoutOnboardingStep?: StripeCheckoutOnboardingStep;
  checkoutSource?: StripeCheckoutSource;
  commercialPlanKey?: CommercialPlanKey;
  extraUsageBlocks?: number;
  location: Location;
  planKind?: SubscriptionPlanKind;
  planVariant?: SubscriptionPlanVariant;
  reminderShillUpsellAttributionContext?: ReminderShillUpsellAttributionContext | null;
  store: AppStore;
}

type CheckoutSessionResponse =
  components["schemas"]["CoreApi__CreateStripeCheckoutSessionResponseSchema"];
type CheckoutSessionNavigationResult = CheckoutSessionResponse & {
  didStartExternalNavigation: boolean;
};
type CreateCheckoutSessionRequest =
  components["schemas"]["CoreApi__CreateStripeCheckoutSessionRequestSchema"] & {
    commercial_plan_key?: CommercialPlanKey;
    plan_kind?: SubscriptionPlanKind;
    plan_variant?: SubscriptionPlanVariant | null;
  };

/**
 * Creates a Stripe checkout session and handles navigation based on the response.
 * Handles three scenarios:
 * 1. User needs to go through Stripe checkout (stripe_checkout_session_url)
 * 2. User already has a subscription and needs billing portal (stripe_billing_portal_url)
 * 3. User has a Stripe or Apple subscription that is already on or moved to the requested plan
 * 4. User has a Mem-managed House subscription (already_paid + House provider)
 */
export async function createCheckoutSessionAndNavigate({
  billingStrategy,
  checkoutContext,
  checkoutOnboardingStep,
  checkoutSource,
  commercialPlanKey,
  extraUsageBlocks,
  location,
  planKind,
  planVariant,
  reminderShillUpsellAttributionContext,
  store,
}: CreateCheckoutSessionParams): Promise<CheckoutSessionNavigationResult> {
  if (
    !commercialPlanKey &&
    isCommercialRolloutActive({
      catalog: store.platformInfo.commercialCatalog,
      snapshot: store.platformInfo.commercialPlanSnapshot,
    })
  ) {
    store.settingsModal.open(SettingsTabId.SubscriptionManagementPage);
    throw new BaseError({
      message: "Choose a current Mem plan in Subscription Management.",
      info: { reason: "commercial_catalog_requires_explicit_plan_key" },
    });
  }

  const {
    original_plan_kind: originalPlanKind,
    original_effective_plan_kind: originalEffectivePlanKind,
  } = buildOriginalSubscriptionPlanMetricsPayload({ store });
  const checkoutReturnContext = commercialPlanKey
    ? StripeCheckoutContext.Commercial
    : checkoutContext;
  const commercialCatalogGeneration = commercialPlanKey
    ? store.platformInfo.commercialCatalog?.generation
    : undefined;
  const successUrl = buildStripeSuccessUrl({
    location,
    checkoutContext: checkoutReturnContext,
    checkoutOnboardingStep,
    checkoutSource,
    originalPlanKind,
    originalEffectivePlanKind,
    commercialCatalogGeneration,
    targetCommercialPlanKey: commercialPlanKey,
  });
  const cancelUrl = buildStripeCancelUrl({
    location,
    checkoutContext: checkoutReturnContext,
    checkoutOnboardingStep,
    checkoutSource,
    originalPlanKind,
    originalEffectivePlanKind,
    commercialCatalogGeneration,
    targetCommercialPlanKey: commercialPlanKey,
  });
  const returnUrl = buildStripeBillingReturnUrl({
    location,
    checkoutContext: checkoutReturnContext,
    checkoutOnboardingStep,
    checkoutSource,
    originalPlanKind,
    originalEffectivePlanKind,
    commercialCatalogGeneration,
    targetCommercialPlanKey: commercialPlanKey,
  }); // used if the checkout gets "upgraded" to a billing portal url by the backend

  const body: CreateCheckoutSessionRequest = {
    cancel_url: cancelUrl,
    success_url: successUrl,
    return_url: returnUrl,
    billing_strategy: billingStrategy,
  };
  if (commercialPlanKey) {
    body.commercial_plan_key = commercialPlanKey;
    body.extra_usage_blocks = extraUsageBlocks ?? 0;
  }
  // `commercial_plan_key` is authoritative for the new catalog. Keep
  // `plan_kind` only for legacy checkout callers until that compatibility
  // field is removed from the request contract entirely.
  if (!commercialPlanKey && planKind) {
    body.plan_kind = planKind;
  }
  if (
    !commercialPlanKey &&
    planKind === SubscriptionPlanKind.PROACTIVE &&
    store.subscriptions.shouldAllowProactiveStandardPrice === true
  ) {
    body.allow_proactive_standard_price = true;
  }
  if (planVariant) {
    body.plan_variant = planVariant;
  }

  const rewardfulReferral = getRewardfulReferral();
  if (rewardfulReferral) {
    body.client_reference_id = rewardfulReferral;
  }

  const response = await api.post("/v2/me/subscriptions/stripe/checkout-session", {
    body,
  });

  if (!response.data) {
    logger.error({
      message: "Failed to create checkout session",
      info: { response: objectModule.safeAsJson(response) },
    });
    throw new BaseError({
      message: "Failed to create checkout session",
      info: { response: objectModule.safeAsJson(response) },
    });
  }

  const {
    stripe_checkout_session_url,
    stripe_billing_portal_url,
    already_paid,
    provider,
    proactive_trial_granted,
  } = response.data;

  // If the backend gave us a billing portal url, use that. It means the user is already subscribed.
  if (stripe_billing_portal_url) {
    writePendingReminderShillUpsellAttribution(reminderShillUpsellAttributionContext);
    await windowModule.navigateToExternal({ url: new URL(stripe_billing_portal_url) });
    return { ...response.data, didStartExternalNavigation: true };
  } else if (stripe_checkout_session_url) {
    writePendingReminderShillUpsellAttribution(reminderShillUpsellAttributionContext);
    await windowModule.navigateToExternal({ url: new URL(stripe_checkout_session_url) });
    return { ...response.data, didStartExternalNavigation: true };
  } else if (already_paid && (isStripeProvider(provider) || isAppleProvider(provider))) {
    // User is already paid; optimistically reflect a legacy requested plan.
    // Commercial checkout is keyed by the server catalog, so its refreshed
    // platform snapshot is the only authoritative plan state.
    if (!commercialPlanKey) {
      store.platformInfo.setSubscriptionCompletedSuccessfully(planKind ?? SubscriptionPlanKind.PRO);
    }
    await store.platformInfo.fetchPlatformInfo();

    // Proactive checkout can upgrade Mem Agent account access server-side.
    // Refresh that cache before deciding whether the dedicated onboarding is available.
    const checkoutBelongsToFloatingMemOnboarding =
      checkoutSource === StripeCheckoutSource.Onboarding;
    if (checkoutBelongsToFloatingMemOnboarding) {
      await store.spaceAccounts.fetchMySpaceAccounts();
    }

    const floatingMemOnboardingOwnsSuccessfulCheckout =
      checkoutSource === StripeCheckoutSource.Onboarding &&
      store.floatingAgent.isOnboardingSurfaceAvailable;

    if (
      !commercialPlanKey &&
      planKind === SubscriptionPlanKind.PROACTIVE &&
      store.subscriptions.billingSubscription?.isStoredProactive === true &&
      !floatingMemOnboardingOwnsSuccessfulCheckout
    ) {
      store.platformInfo.openWelcomeToProactiveModal();
    }

    if (reminderShillUpsellAttributionContext) {
      await recordReminderShillUpsellAttribution({
        context: reminderShillUpsellAttributionContext,
      });
      await dismissReminderShillUpsellAttributionContext({
        context: reminderShillUpsellAttributionContext,
        store,
      });
    }
    return { ...response.data, didStartExternalNavigation: false };
  } else if (already_paid && isHouseProvider(provider)) {
    throw new BaseError({
      message: "This subscription is managed by Mem. Contact support to change plans.",
      info: { response: objectModule.safeAsJson(response) },
    });
  } else if (
    !commercialPlanKey &&
    planKind === SubscriptionPlanKind.PROACTIVE &&
    proactive_trial_granted === false
  ) {
    await store.platformInfo.fetchPlatformInfo();
    toastModule.triggerToast({
      content: "Your Proactive trial eligibility changed. Review the current price and try again.",
    });
    return { ...response.data, didStartExternalNavigation: false };
  } else {
    logger.error({
      message: "Failed to create checkout session. Unexpected response from endpoint.",
      info: { response: objectModule.safeAsJson(response) },
    });
    throw new BaseError({
      message: "Failed to create checkout session. Unexpected response from endpoint.",
      info: { response: objectModule.safeAsJson(response) },
    });
  }
}
